<template>
  <div v-if="versionInfo" class="version-badge" :class="[`version-badge-${versionInfo.environment}`]">
    <el-tooltip
      placement="top-end"
      :content="tooltipContent"
      popper-class="version-tooltip"
    >
      <span class="version-badge-text">{{ versionInfo.fullVersion }}</span>
    </el-tooltip>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { getCurrentVersion } from '@/api/version'
import type { CurrentVersionInfo } from '@/types/version'

const versionInfo = ref<CurrentVersionInfo | null>(null)

const tooltipContent = computed(() => {
  if (!versionInfo.value) return ''
  const info = versionInfo.value
  return [
    `版本号: ${info.version}`,
    `完整版本: ${info.fullVersion}`,
    `Git提交: ${info.commit}`,
    `分支: ${info.branch}`,
    `构建时间: ${info.buildTime}`,
    `提交说明: ${info.commitMessage}`,
  ].join('\n')
})

onMounted(async () => {
  try {
    const data = await getCurrentVersion()
    versionInfo.value = data
  } catch {
    // API 失败时优雅隐藏
    versionInfo.value = null
  }
})
</script>

<style scoped>
.version-badge {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 999;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  cursor: default;
  user-select: none;
  /* 默认颜色(staging/test等未知环境)，被 -dev/-prod 覆盖 */
  background: rgba(144, 147, 153, 0.12);
  color: var(--color-text-secondary);
  border: 1px solid rgba(144, 147, 153, 0.25);
}

.version-badge:hover {
  opacity: 1;
}

/* 环境颜色：dev = 蓝色 */
.version-badge-dev {
  background: rgba(35, 131, 226, 0.12);
  color: #2383e2;
  border: 1px solid rgba(35, 131, 226, 0.25);
}

/* 环境颜色：prod = 绿色 */
.version-badge-prod {
  background: rgba(39, 174, 96, 0.12);
  color: #27ae60;
  border: 1px solid rgba(39, 174, 96, 0.25);
}

.version-badge-text {
  font-family: 'Courier New', Courier, monospace;
  white-space: nowrap;
}
</style>

<style>
/* 全局 tooltip 样式：等宽字体显示版本信息 */
.version-tooltip {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.8;
  white-space: pre-line;
}
</style>
